import React, { useEffect, useState } from 'react';
import { AiOutlineClockCircle } from 'react-icons/ai';
import { IoClose } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import Header from '../component/Header';
import BackgroundImage from '../images/tirvia/trivia-bg.png';

const WordSearch = () => {
  const [showIntro, setShowIntro] = useState(true);
  const [started, setStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120); // Changed from 180 to 120 seconds (2 minutes)
  const [selectedCells, setSelectedCells] = useState([]);
  const [foundWords, setFoundWords] = useState([]);
  const [showCompletion, setShowCompletion] = useState(false);
  const [showTimeout, setShowTimeout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [gameConfig, setGameConfig] = useState(null);
  const [gameData, setGameData] = useState({
    grid: [],
    words: [],
    batchId: null,
    sessionId: '',
    startTime: null
  });
  
  const [wordPositions, setWordPositions] = useState({}); // Store word positions
  const navigate = useNavigate();

  // Fetch game config on component mount with delay
  useEffect(() => {
    const fetchGameConfig = async () => {
      // Add 5-second delay before fetching
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Please login to play this game');
        }

        const myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Authorization", `Bearer ${token}`);

        const response = await fetch("https://devapi.quickcare.ng/api/v1/games/word_search/config", {
          method: "GET",
          headers: myHeaders,
          redirect: "follow"
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to fetch game config');
        }

        const result = await response.json();
        
        if (!result.success || !result.data) {
          throw new Error('Invalid response from server');
        }
        
        if (!result.data.can_access) {
          throw new Error('Access to this game is currently restricted');
        }

        if (result.data.attempts <= 0) {
          throw new Error('No attempts remaining. Please try again later.');
        }

        setGameConfig(result.data);
      } catch (err) {
        console.error('Config error:', err);
        setError(err.message || 'Failed to load game. Please try again later.');
        // Auto-redirect after 3 seconds
        setTimeout(() => {
          navigate('/trivia-games');
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    fetchGameConfig();
  }, [navigate]);

  // Function to find word positions in grid (8 directions)
  const findWordPositions = (grid, word) => {
    const rows = grid.length;
    const cols = grid[0].length;
    const wordUpper = word.toUpperCase();
    const directions = [
      [0, 1],   // horizontal right
      [1, 0],   // vertical down
      [1, 1],   // diagonal down-right
      [1, -1],  // diagonal down-left
      [0, -1],  // horizontal left
      [-1, 0],  // vertical up
      [-1, -1], // diagonal up-left
      [-1, 1],  // diagonal up-right
    ];

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        for (const [dr, dc] of directions) {
          const positions = [];
          let found = true;
          
          for (let i = 0; i < wordUpper.length; i++) {
            const nr = r + dr * i;
            const nc = c + dc * i;
            
            if (
              nr < 0 || nr >= rows ||
              nc < 0 || nc >= cols ||
              grid[nr][nc] !== wordUpper[i]
            ) {
              found = false;
              break;
            }
            
            positions.push([nr, nc]);
          }
          
          if (found && positions.length === wordUpper.length) {
            return positions;
          }
        }
      }
    }
    
    console.warn(`Word "${word}" not found in grid`);
    return [];
  };

  // Start game session
  const startGameSession = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Please login to play this game');
      }

      const myHeaders = new Headers();
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      const response = await fetch("https://devapi.quickcare.ng/api/v1/games/word_search/start", {
        method: "POST",
        headers: myHeaders,
        redirect: "follow"
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to start game session');
      }

      const result = await response.json();

      if (!result.data) {
        throw new Error('Invalid game data received from server');
      }

      const grid = result.data.puzzle || [];
      const words = result.data.words || [];
      
      if (grid.length === 0 || grid[0].length === 0) {
        throw new Error('Invalid puzzle grid received');
      }

      // Find positions for each word and store them
      const positionsMap = {};
      words.forEach(word => {
        positionsMap[word] = findWordPositions(grid, word);
      });

      setWordPositions(positionsMap);
      
      // Store words as objects with found status
      const wordsWithStatus = words.map(word => ({
        text: word,
        found: false
      }));

      setGameData({
        grid: grid,
        words: wordsWithStatus,
        batchId: result.data.batch_id,
        sessionId: result.data.session_id,
        startTime: new Date(result.data.start_time)
      });

      setShowIntro(false);
      setStarted(true);
      setShowCompletion(false);
      setShowTimeout(false);
      setSelectedCells([]);
      setFoundWords([]);
      setTimeLeft(120); // Reset to 2 minutes
      
    } catch (err) {
      console.error('Error starting game:', err);
      setError(err.message || 'Failed to start game. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Timer effect - 2 minutes
  useEffect(() => {
    if (!started || showCompletion || showTimeout) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setShowTimeout(true);
          setStarted(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [started, showCompletion, showTimeout]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper function to compare arrays of positions
  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    
    for (let i = 0; i < sortedA.length; i++) {
      if (sortedA[i] !== sortedB[i]) return false;
    }
    return true;
  };

  // Check if selected cells match any word
  const checkSelectionForWord = (selectedCellsArray) => {
    if (selectedCellsArray.length < 2) return null;
    
    // Sort selected cells for consistent comparison
    const sortedSelected = [...selectedCellsArray].sort();
    
    // Check each word that hasn't been found yet
    for (const word of gameData.words) {
      if (word.found) continue;
      
      const wordPos = wordPositions[word.text];
      if (!wordPos || wordPos.length === 0) continue;
      
      // Convert word positions to cell keys
      const wordCellKeys = wordPos.map(pos => `${pos[0]}-${pos[1]}`).sort();
      
      // Check if arrays match (same positions)
      if (arraysEqual(sortedSelected, wordCellKeys)) {
        return word.text;
      }
    }
    
    return null;
  };

  // Handle cell click
  const handleCellClick = (rowIndex, colIndex) => {
    if (!started || showCompletion || showTimeout) return;

    const key = `${rowIndex}-${colIndex}`;
    
    setSelectedCells(prev => {
      // Check if cell is already selected
      const isSelected = prev.includes(key);
      let newSelection;
      
      if (isSelected) {
        // Remove cell from selection
        newSelection = prev.filter(cell => cell !== key);
      } else {
        // Add cell to selection
        newSelection = [...prev, key];
      }
      
      // Check if we found a word
      const foundWord = checkSelectionForWord(newSelection);
      if (foundWord && !foundWords.includes(foundWord)) {
        // Mark word as found
        const updatedFoundWords = [...foundWords, foundWord];
        setFoundWords(updatedFoundWords);
        
        // Update word status
        setGameData(prev => ({
          ...prev,
          words: prev.words.map(w => 
            w.text === foundWord ? { ...w, found: true } : w
          )
        }));
        
        console.log(`Found word: ${foundWord}. Total found: ${updatedFoundWords.length}/${gameData.words.length}`);
        
        // Clear selection after finding word
        return [];
      }
      
      return newSelection;
    });
  };

  const handleStart = async () => {
    if (!gameConfig) return;
    await startGameSession();
  };

  // Get start and end coordinates for a word
  const getStartEndCoordinates = (positions) => {
    if (!positions || positions.length === 0) return [[0, 0], [0, 0]];
    
    // Get first and last position
    const startPos = positions[0];
    const endPos = positions[positions.length - 1];
    
    return [startPos, endPos];
  };

  // Submit answers to server
  const handleSubmit = async () => {
    const totalWords = gameData.words.length;
    const foundCount = foundWords.length;
    
    if (foundCount < totalWords) {
      alert(`You need to find all ${totalWords} words! You've found ${foundCount} of ${totalWords}.`);
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Accept", "application/json");
      myHeaders.append("Authorization", `Bearer ${token}`);

      // Prepare answers in the format: [[[startRow, startCol], [endRow, endCol]], ...]
      const answers = gameData.words.map(word => {
        const positions = wordPositions[word.text] || [];
        return getStartEndCoordinates(positions);
      });

      const submissionData = {
        answers: answers,
        session_id: gameData.sessionId
      };

      console.log('Submitting data:', JSON.stringify(submissionData, null, 2));

      const raw = JSON.stringify(submissionData);

      const requestOptions = {
        method: "POST",
        headers: myHeaders,
        body: raw,
        redirect: "follow"
      };

      const response = await fetch("https://devapi.quickcare.ng/api/v1/games/word_search/answer", requestOptions);
      
      console.log('Response status:', response.status);
      
      // Get response text first to see what's coming back
      const responseText = await response.text();
      console.log('Response text:', responseText);
      
      let result;
      try {
        result = JSON.parse(responseText);
        console.log('Parsed response:', result);
      } catch (e) {
        console.error('Failed to parse response as JSON:', e);
        throw new Error('Invalid response from server');
      }
      
      if (!response.ok) {
        console.error('Server error response:', result);
        throw new Error(result.message || `Failed to submit answers. Status: ${response.status}`);
      }

      setShowCompletion(true);
      setStarted(false);
      
    } catch (err) {
      console.error('Error submitting game:', err);
      alert(`Failed to submit results: ${err.message}. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseIntro = () => navigate('/trivia-games');
  const attemptsLeft = gameConfig ? gameConfig.attempts : 0;
  const totalWords = gameData.words.length;
  const allWordsFound = foundWords.length === totalWords && totalWords > 0;

  if (loading && !started) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#069125] mx-auto mb-4"></div>
          <p className="text-gray-700">Preparing your game...</p>
          <p className="text-gray-500 text-sm mt-2">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center bg-[#F9FAFB] p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Unable to Start Game</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <p className="text-gray-500 text-sm mb-4">Redirecting to games page...</p>
            <button
              onClick={() => navigate('/trivia-games')}
              className="bg-[#069125] text-white py-2 px-6 rounded-lg hover:bg-[#057a1f] transition-colors"
            >
              Back to Games
            </button>
          </div>
        </div>
      </div>
    );
  }

  const successMessage = `You found ${foundWords.length} of ${totalWords} words. ${attemptsLeft > 0 ? `You have ${attemptsLeft} attempt${attemptsLeft !== 1 ? 's' : ''} left.` : ''}`;
  const timeoutMessage = `Your 2-minute timer expired. Try again to find all ${totalWords} words.`;

  return (
    <div className='w-full flex items-start justify-center bg-[#F9FAFB] relative'>
      <div className='flex flex-col w-full'>
        <Header title='Word Search' />

        <div
          className='min-h-screen p-4 bg-cover bg-center bg-no-repeat flex items-center justify-center'
          style={{ backgroundImage: `url(${BackgroundImage})` }}
        >
          {showIntro ? (
            <div className='w-full max-w-xl px-4'>
              <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl relative'>
                <button
                  onClick={handleCloseIntro}
                  className='absolute top-4 right-4 text-white hover:text-gray-300 transition-colors'
                >
                  <IoClose className='text-2xl' />
                </button>

                <div className='text-center mb-6 mt-4'>
                  <h1 className='text-xl md:text-[32px] instrument-sans-bold text-white'>
                    Word Search
                  </h1>
                </div>

                <p className='text-white text-center text-sm md:text-base instrument-sans-normal'>
                  Find all hidden words in the grid within 2 minutes.
                  <br />
                  <span className='font-bold mt-2 block'>You have 2 minutes to complete the game!</span>
                </p>

                {loading ? (
                  <div className='mt-6 mb-8'>
                    <div className='flex flex-col items-center gap-2'>
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                      <p className='text-white text-center text-sm md:text-base instrument-sans-semibold'>
                        Loading game configuration...
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className='mt-6 mb-8'>
                    <p className='text-white text-center text-sm md:text-base instrument-sans-semibold'>
                      Note: You have {attemptsLeft} attempt{attemptsLeft !== 1 ? 's' : ''} left.
                    </p>
                  </div>
                )}

                <div className='flex justify-center'>
                  <button
                    onClick={handleStart}
                    disabled={loading || !gameConfig}
                    className='bg-[#069125] hover:bg-white hover:text-black text-white py-3 px-12 rounded-lg transition-colors duration-200 instrument-sans-semibold w-full disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    {loading ? 'Loading...' : 'Play'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className='w-full px-4'>
              <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl'>
                <div className='flex flex-col items-center mb-6 gap-2'>
                  <div className='flex items-center gap-2 text-white'>
                    <AiOutlineClockCircle className='text-xl' />
                    <span className='text-base md:text-lg instrument-sans-bold'>
                      {formatTime(timeLeft)}
                    </span>
                  </div>
                  <p className='text-white/80 text-sm instrument-sans-medium text-center'>
                    Find {totalWords} hidden words in 2 minutes
                  </p>
                  <p className='text-white/60 text-xs instrument-sans-normal text-center'>
                    {foundWords.length} of {totalWords} words found
                  </p>
                </div>

                <div className='bg-white/10 rounded-xl p-4 md:p-6 flex flex-col items-center'>
                  <h2 className='text-white text-center instrument-sans-bold text-lg md:text-xl mb-4'>
                    Click letters to find words
                  </h2>

                  <div className="mb-4">
                    <p className="text-white/80 text-sm instrument-sans-medium text-center">
                      Click on letters in order to select a word. Selected cells turn yellow.
                    </p>
                  </div>

                  {gameData.grid.length > 0 ? (
                    <div 
                      className='grid gap-1 md:gap-2 mb-6'
                      style={{ 
                        gridTemplateColumns: `repeat(${gameData.grid[0].length}, minmax(40px, 40px))`,
                        maxWidth: '100%',
                        overflowX: 'auto'
                      }}
                    >
                      {gameData.grid.map((row, rowIndex) =>
                        row.map((letter, colIndex) => {
                          const key = `${rowIndex}-${colIndex}`;
                          const isSelected = selectedCells.includes(key);
                          return (
                            <button
                              key={key}
                              onClick={() => handleCellClick(rowIndex, colIndex)}
                              className={`w-7 h-7 md:w-9 md:h-9 rounded-md text-sm md:text-base font-semibold transition-all duration-150 border ${
                                isSelected
                                  ? 'bg-yellow-400 text-[#0F421C] border-yellow-500'
                                  : 'bg-transparent text-white hover:bg-white/10 border-white/30'
                              }`}
                            >
                              {letter}
                            </button>
                          );
                        })
                      )}
                    </div>
                  ) : (
                    <div className="text-white text-center p-4">
                      <p>No game data available. Please try again.</p>
                    </div>
                  )}

                  <div className='w-full max-w-3xl border-t border-white/20 pt-4'>
                    <h3 className='text-white text-center instrument-sans-bold mb-4'>
                      Words to Find ({foundWords.length}/{totalWords})
                    </h3>
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-white text-sm instrument-sans-semibold'>
                      {gameData.words.length > 0 ? (
                        gameData.words.map((word) => {
                          const isFound = word.found;
                          return (
                            <div
                              key={word.text}
                              className={`flex items-center gap-2 p-3 rounded-lg transition-all duration-300 ${
                                isFound 
                                  ? 'bg-[#0AC24B]/20 text-[#0AC24B] border-2 border-[#0AC24B]/40 transform scale-105' 
                                  : 'bg-white/5 text-white hover:bg-white/10'
                              }`}
                            >
                              <span
                                className={`w-3 h-3 rounded-full flex-shrink-0 ${
                                  isFound ? 'bg-[#0AC24B] animate-pulse' : 'bg-white/60'
                                }`}
                              />
                              <span className={isFound ? 'font-bold line-through' : 'font-medium'}>
                                {word.text}
                              </span>
                              {isFound && (
                                <span className="ml-auto text-xs bg-[#0AC24B] text-white px-2 py-1 rounded-full animate-bounce">
                                  ✓ Found
                                </span>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-white/70">No words to find</p>
                      )}
                    </div>
                  </div>

                  <div className='flex justify-end items-center w-full mt-6 gap-4'>
                    <span className="text-white instrument-sans-medium">
                      Found: {foundWords.length}/{totalWords}
                    </span>
                    <button
                      onClick={handleSubmit}
                      disabled={!allWordsFound || submitting}
                      className={`${
                        allWordsFound 
                          ? 'bg-[#0AC24B] hover:bg-[#09b042] animate-pulse' 
                          : 'bg-[#069125] hover:bg-[#057a1f]'
                      } text-white instrument-sans-medium py-3 px-8 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {submitting ? 'Submitting...' : 'Submit Answer'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showCompletion && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <div className='w-full max-w-xl px-4'>
            <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl'>
              <div className='text-center mb-6'>
                <h3 className='text-xl md:text-2xl instrument-sans-bold text-white'>Great job!</h3>
                <div className="text-5xl mb-4">🎉</div>
              </div>
              <p className='text-white text-center text-sm md:text-base instrument-sans-normal mb-6'>
                {successMessage}
              </p>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                <button
                  onClick={() => navigate('/trivia-games')}
                  className='bg-white text-[#0F421C] py-3 rounded-lg instrument-sans-semibold hover:bg-white/90 transition-colors duration-200'
                >
                  Back to games
                </button>
                <button
                  onClick={handleStart}
                  className='bg-[#069125] text-white py-3 rounded-lg instrument-sans-semibold hover:bg-[#057a1f] transition-colors duration-200'
                >
                  Play again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTimeout && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <div className='w-full max-w-xl px-4'>
            <div className='bg-[#308042B2]/70 rounded-lg p-6 md:p-8 shadow-xl'>
              <div className='text-center mb-6'>
                <h3 className='text-xl md:text-2xl instrument-sans-bold text-white'>Time's up!</h3>
                <div className="text-5xl mb-4">⏰</div>
              </div>
              <p className='text-white text-center text-sm md:text-base instrument-sans-normal mb-6'>
                {timeoutMessage}
              </p>
              <p className='text-white/80 text-center text-sm instrument-sans-normal mb-6'>
                You found {foundWords.length} of {totalWords} words.
              </p>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                <button
                  onClick={() => navigate('/trivia-games')}
                  className='bg-white text-[#0F421C] py-3 rounded-lg instrument-sans-semibold hover:bg-white/90 transition-colors duration-200'
                >
                  Back to games
                </button>
                <button
                  onClick={handleStart}
                  className='bg-[#069125] text-white py-3 rounded-lg instrument-sans-semibold hover:bg-[#057a1f] transition-colors duration-200'
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WordSearch;